import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Book, BookFilters } from '../types';
import { booksApi, tagsApi, authApi, TagsByCategory, AuthUser } from '../services/api';
import { FilterPanel } from './FilterPanel';
import { BookGrid } from './BookGrid';
import { BookDetailModal } from './BookDetailModal';
import './BookCatalogView.css';

/**
 * Componente BookCatalogView
 * 
 * Exibe o catálogo de projetos para clientes com:
 * - Login obrigatório para visualização
 * - Painel de filtros (FilterPanel)
 * - Grade de projetos (BookGrid)
 * - Modal de detalhes (BookDetailModal)
 * - Estados de loading e erro
 */
export function BookCatalogView(): React.ReactElement {
  // Estado de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);

  // Verificar sessão ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const status = await authApi.getStatus();
        if (status.isAuthenticated && status.user) {
          setIsAuthenticated(true);
          setCurrentUser(status.user);
        }
      } catch {
        // Não autenticado
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const result = await authApi.login(loginUsername, loginPassword);
      setIsAuthenticated(true);
      setCurrentUser(result.user);
    } catch (err: any) {
      setLoginError(err?.message || 'Usuário ou senha inválidos');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignora erro
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setLoginUsername('');
    setLoginPassword('');
  };

  // Tela de loading inicial
  if (isCheckingAuth) {
    return (
      <div className="book-catalog-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="book-grid__loading-container">
          <div className="book-grid__spinner" />
          <p className="book-grid__loading-text">Carregando...</p>
        </div>
      </div>
    );
  }

  // Tela de login
  if (!isAuthenticated) {
    return (
      <div className="book-catalog-view">
        <header className="book-catalog-view__header">
          <div className="book-catalog-view__header-left">
            <img src="/logo-ipsis-pack.png" alt="Ipsis Pack" className="book-catalog-view__logo" />
          </div>
        </header>
        <div className="book-catalog-view__login-wrap">
          <div className="book-catalog-view__login-card">
            <h2 className="book-catalog-view__login-title">Estante Ipsis Pack</h2>
            <p className="book-catalog-view__login-subtitle">Faça login para acessar o catálogo</p>
            <form onSubmit={handleLogin}>
              {loginError && (
                <div className="book-catalog-view__login-error">
                  {loginError}
                </div>
              )}
              <div className="book-catalog-view__login-field">
                <label htmlFor="catalog-username" className="book-catalog-view__login-label">Usuário</label>
                <input
                  id="catalog-username"
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  placeholder="Digite seu usuário"
                  disabled={isLoggingIn}
                  required
                  className="book-catalog-view__login-input"
                />
              </div>
              <div className="book-catalog-view__login-field">
                <label htmlFor="catalog-password" className="book-catalog-view__login-label">Senha</label>
                <input
                  id="catalog-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  disabled={isLoggingIn}
                  required
                  className="book-catalog-view__login-input"
                />
              </div>
              <button
                type="submit"
                disabled={isLoggingIn || !loginUsername.trim() || !loginPassword.trim()}
                className="book-catalog-view__login-submit"
              >
                {isLoggingIn ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Catálogo (autenticado)
  return <BookCatalogContent currentUser={currentUser} onLogout={handleLogout} />;
}

// Componente interno com o conteúdo do catálogo
function BookCatalogContent({ currentUser, onLogout }: { currentUser: AuthUser | null; onLogout: () => void }): React.ReactElement {
  // Estoque Mauá é dado interno — restrito a admin (a API também bloqueia)
  const isAdmin = currentUser?.role === 'admin';
  // Estado dos livros (todos os livros carregados)
  const [allBooks, setAllBooks] = useState<Book[]>([]);
  const [isLoadingBooks, setIsLoadingBooks] = useState(true);
  const [booksError, setBooksError] = useState<string | null>(null);

  // Estado das tags para filtros
  const [tagsByCategory, setTagsByCategory] = useState<TagsByCategory[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);

  // Estado dos filtros ativos
  const [activeFilters, setActiveFilters] = useState<BookFilters>({});

  // Paginação client-side: a lista inteira já está carregada (contadores do
  // painel de tags precisam dela); paginar só evita renderizar/consultar
  // centenas de thumbnails de uma vez
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 24;

  // Deep-link ?book=ID (ex.: card do estoque → projeto no catálogo)
  const [searchParams, setSearchParams] = useSearchParams();

  // Estado do modal de detalhes
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Estado do painel de filtros (mobile)
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Carrega tags ao montar o componente
  useEffect(() => {
    const loadTags = async () => {
      setIsLoadingTags(true);
      try {
        const data = await tagsApi.getAllByCategory();
        setTagsByCategory(data);
      } catch (error) {
        console.error('Erro ao carregar tags:', error);
      } finally {
        setIsLoadingTags(false);
      }
    };
    loadTags();
  }, []);

  // Carrega todos os livros uma vez ao montar
  const loadBooks = useCallback(async () => {
    setIsLoadingBooks(true);
    setBooksError(null);
    try {
      const data = await booksApi.getAll();
      setAllBooks(data);
    } catch (error) {
      const errorMessage =
        (error as { message?: string })?.message || 'Erro ao carregar projetos. Tente novamente.';
      setBooksError(errorMessage);
    } finally {
      setIsLoadingBooks(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  // Filtra livros localmente (sem chamar API)
  const filteredBooks = useMemo(() => {
    let result = allBooks;

    // Filtro por texto (título ou descrição)
    if (activeFilters.search && activeFilters.search.trim()) {
      const searchTerm = activeFilters.search.trim().toLowerCase();
      result = result.filter(book => 
        book.title.toLowerCase().includes(searchTerm) ||
        book.description.toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por disponibilidade física
    if (activeFilters.physicallyAvailable !== undefined) {
      result = result.filter(book => book.physicallyAvailable === activeFilters.physicallyAvailable);
    }

    // Filtro por livros premiados
    if (activeFilters.isAwarded !== undefined) {
      result = result.filter(book => book.isAwarded === activeFilters.isAwarded);
    }

    // Filtro por tipo de caixa
    if (activeFilters.boxType) {
      result = result.filter(book => book.boxType === activeFilters.boxType);
    }

    // Filtro por tags
    if (activeFilters.tags && activeFilters.tags.length > 0) {
      result = result.filter(book => {
        if (!book.tags) return false;
        const bookTagIds = book.tags.map(tag => tag.id);
        return activeFilters.tags!.every(tagId => bookTagIds.includes(tagId));
      });
    }

    return result;
  }, [allBooks, activeFilters]);

  // Recalcula contadores das tags baseado nos livros filtrados
  const tagsWithDynamicCounts = useMemo(() => {
    return tagsByCategory.map(categoryData => ({
      ...categoryData,
      tags: categoryData.tags.map(tag => ({
        ...tag,
        bookCount: filteredBooks.filter(book =>
          book.tags?.some(t => t.id === tag.id)
        ).length
      }))
    }));
  }, [tagsByCategory, filteredBooks]);

  // Handler para mudança de filtros (volta para a 1ª página: a página atual
  // pode não existir mais com o novo conjunto de resultados)
  const handleFilterChange = useCallback((filters: BookFilters) => {
    setActiveFilters(filters);
    setPage(1);
  }, []);

  // Handler para clique em livro (sincroniza o deep-link ?book=ID)
  const handleBookClick = useCallback((book: Book) => {
    setSelectedBook(book);
    setIsModalOpen(true);
    setSearchParams({ book: String(book.id) }, { replace: true });
  }, [setSearchParams]);

  // Handler para fechar modal (remove o deep-link para um refresh não reabrir)
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBook(null);
    if (searchParams.has('book')) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep-link: ?book=ID abre o modal do projeto (chegando do estoque, ou link
  // compartilhado). Roda após os livros carregarem; ID inexistente limpa o param.
  // Se a carga FALHOU (booksError), não decide nada — o param fica na URL para o
  // "Tentar novamente" reabrir o modal depois de recuperar
  useEffect(() => {
    const bookParam = searchParams.get('book');
    if (!bookParam || isLoadingBooks || booksError) return;
    const bookId = Number(bookParam);
    const book = allBooks.find(b => b.id === bookId);
    if (book) {
      setSelectedBook(book);
      setIsModalOpen(true);
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, allBooks, isLoadingBooks, booksError, setSearchParams]);

  // Página exibida (clamp: filtros podem encolher a lista embaixo da página atual)
  const pageCount = Math.max(1, Math.ceil(filteredBooks.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedBooks = useMemo(
    () => filteredBooks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredBooks, currentPage]
  );

  // Handler para toggle do painel de filtros (mobile)
  const toggleFilterPanel = useCallback(() => {
    setIsFilterPanelOpen((prev) => !prev);
  }, []);

  // Conta filtros ativos
  const activeFilterCount =
    (activeFilters.tags?.length || 0) +
    (activeFilters.physicallyAvailable !== undefined ? 1 : 0) +
    (activeFilters.isAwarded !== undefined ? 1 : 0) +
    (activeFilters.boxType ? 1 : 0) +
    (activeFilters.search?.trim() ? 1 : 0);

  return (
    <div className="book-catalog-view">
      {/* Header */}
      <header className="book-catalog-view__header">
        <div className="book-catalog-view__header-left">
          <img 
            src="/logo-ipsis-pack.png" 
            alt="Ipsis Pack" 
            className="book-catalog-view__logo"
          />
        </div>
        <div className="book-catalog-view__user">
          <span className="book-catalog-view__user-name">
            Olá, {currentUser?.displayName || currentUser?.username}
          </span>
          {/* Atalho para o painel administrativo — apenas admin (a API também bloqueia) */}
          {isAdmin && (
            <Link to="/admin" className="book-catalog-view__admin-link" title="Área de Admin">
              <span className="book-catalog-view__admin-link-icon">⚙️</span>
              <span className="book-catalog-view__admin-link-text">Área de Admin</span>
            </Link>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="book-catalog-view__logout-button"
          >
            Sair
          </button>
          <button
            type="button"
            className="book-catalog-view__filter-toggle"
            onClick={toggleFilterPanel}
            aria-expanded={isFilterPanelOpen}
            aria-controls="filter-panel"
          >
            <span className="book-catalog-view__filter-icon">🔍</span>
            <span className="book-catalog-view__filter-text">Filtros</span>
            {activeFilterCount > 0 && (
              <span className="book-catalog-view__filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </header>

      <div className="book-catalog-view__content">
        {/* Painel de Filtros */}
        <aside
          id="filter-panel"
          className={`book-catalog-view__sidebar ${isFilterPanelOpen ? 'book-catalog-view__sidebar--open' : ''}`}
        >
          <div className="book-catalog-view__sidebar-header">
            <h2 className="book-catalog-view__sidebar-title">Filtros</h2>
            <button
              type="button"
              className="book-catalog-view__sidebar-close"
              onClick={toggleFilterPanel}
              aria-label="Fechar filtros"
            >
              ✕
            </button>
          </div>
          {/* Atalho para o estoque de exemplares (Mauá) — apenas admin */}
          {isAdmin && (
            <Link to="/estoque" className="book-catalog-view__stock-link">
              <span className="book-catalog-view__stock-link-icon">📦</span>
              <span className="book-catalog-view__stock-link-text">Estoque Mauá</span>
              <span className="book-catalog-view__stock-link-arrow">→</span>
            </Link>
          )}
          {!isLoadingTags && (
            <FilterPanel
              availableTags={tagsWithDynamicCounts}
              onFilterChange={handleFilterChange}
              bookCount={filteredBooks.length}
              isLoading={isLoadingBooks}
              onClose={toggleFilterPanel}
            />
          )}
        </aside>

        {/* Overlay para mobile quando filtros estão abertos */}
        {isFilterPanelOpen && (
          <div
            className="book-catalog-view__overlay"
            onClick={toggleFilterPanel}
            aria-hidden="true"
          />
        )}

        {/* Área principal com grade de livros */}
        <main className="book-catalog-view__main">
          {booksError ? (
            <div className="book-catalog-view__error">
              <span className="book-catalog-view__error-icon">⚠️</span>
              <p className="book-catalog-view__error-message">{booksError}</p>
              <button
                type="button"
                className="book-catalog-view__retry-button"
                onClick={loadBooks}
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            <>
              <BookGrid
                books={pagedBooks}
                onBookClick={handleBookClick}
                isLoading={isLoadingBooks}
                emptyMessage={
                  activeFilterCount > 0
                    ? 'Nenhum projeto encontrado com os filtros selecionados.'
                    : 'Nenhum projeto cadastrado ainda.'
                }
              />
              {pageCount > 1 && (
                <nav className="book-catalog-view__pagination" aria-label="Paginação do catálogo">
                  <button
                    type="button"
                    className="book-catalog-view__pagination-button"
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    ← Anterior
                  </button>
                  <span className="book-catalog-view__pagination-info">
                    Página {currentPage} de {pageCount}
                    <span className="book-catalog-view__pagination-count">
                      {filteredBooks.length} {filteredBooks.length === 1 ? 'projeto' : 'projetos'}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="book-catalog-view__pagination-button"
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage >= pageCount}
                  >
                    Próxima →
                  </button>
                </nav>
              )}
            </>
          )}
        </main>
      </div>

      {/* Modal de Detalhes do Livro (estoque no modal é só para admin) */}
      <BookDetailModal
        book={selectedBook}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        showStock={isAdmin}
      />
    </div>
  );
}

export default BookCatalogView;
